import { useEffect, useMemo, useState } from 'react'

import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'

type Options = {
  clearOnUnmount?: boolean
  placeholderDataKeepPrevious?: boolean
  forceFreshOnMount?: boolean
  requireVariables?: boolean
}

export const useStableReadQuery = <TData, TVariables extends unknown | undefined = undefined>(
  keyRoot: string,
  variables: TVariables,
  queryFn: (vars: TVariables) => Promise<TData>,
  options?: Options
) => {
  const queryClient = useQueryClient()

  const queryKey = useMemo(() => [keyRoot, variables] as const, [keyRoot, variables])

  const exactCached = queryClient.getQueryData<TData>(queryKey)
  const anyCached = queryClient.getQueriesData<TData>({ queryKey: [keyRoot] }).find(([, d]) => !!d)?.[1]

  const initialData = exactCached ?? (variables === undefined ? (anyCached as TData | undefined) : undefined)
  const enabled = options?.requireVariables ? variables !== undefined : variables !== undefined || !initialData

  const result = useQuery<TData>({
    queryKey,
    queryFn: () => queryFn(variables),
    enabled,
    initialData,
    placeholderData: options?.placeholderDataKeepPrevious ? keepPreviousData : undefined,
    staleTime: options?.forceFreshOnMount ? 0 : undefined,
    gcTime: options?.forceFreshOnMount ? 0 : undefined,
    refetchOnMount: options?.forceFreshOnMount ? 'always' : undefined
  })

  // Listen-only mode: if variables are required but undefined, do not fetch,
  // but keep data in sync with any query under the same keyRoot.
  const listenOnly = options?.requireVariables && variables === undefined
  const [proxyData, setProxyData] = useState<TData | undefined>(
    listenOnly ? (anyCached as TData | undefined) : undefined
  )

  useEffect(() => {
    if (!listenOnly) return
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const latest = queryClient.getQueriesData<TData>({ queryKey: [keyRoot] }).find(([, d]) => !!d)?.[1]
      setProxyData(latest)
    })
    return unsubscribe
  }, [listenOnly, queryClient, keyRoot])

  useEffect(() => {
    if (!options?.clearOnUnmount) return
    return () => {
      queryClient.removeQueries({ queryKey, exact: true })
    }
  }, [options?.clearOnUnmount, queryClient, queryKey])

  if (listenOnly) {
    return {
      ...result,
      data: proxyData,
      isFetched: !!proxyData
    }
  }

  return result
}
